import { useEffect, useState } from "react";
import { ChevronDown, Telescope, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    loadAssistantDraft,
    saveAssistantDraft,
    clearAssistantDraft,
} from "../lib/assistantDraftStore";
import { useChatAttachments, CHAT_ATTACHMENT_ACCEPT } from "../hooks/useChatAttachments";
import { ChatComposer, type ChatComposerLabels } from "./v2/ChatComposer";
import { Button } from "./v2/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from "./v2/ui/dropdown-menu";

interface AssistantChatInputProps {
    /**
     * Called with the composed text and, when the operator attached files, the
     * ids of the staged uploads to bind to the turn. The second argument is
     * optional so hosts that do not offer attachments need no change.
     */
    onSubmit: (text: string, attachmentIds?: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    isSending?: boolean;
    /** An answer is being generated: send stays busy until it is done. */
    generating?: boolean;
    size?: "sm" | "md";
    /**
     * Stable key identifying the chat context (ticket / global conversation).
     * When set, the unsent input text is persisted to localStorage under this
     * key and restored on mount, so a host-page hard refresh doesn't discard
     * what the user was typing. Omit to disable persistence (default).
     */
    draftKey?: string;
    /**
     * When true, render the answer-mode menu ("Schnelle Antwort" /
     * "Vertiefte Recherche"). The host owns the state via `researchMode` /
     * `onResearchModeChange` so it can be persisted per conversation. Omit to
     * hide the control entirely (default).
     */
    showResearchToggle?: boolean;
    researchMode?: boolean;
    onResearchModeChange?: (enabled: boolean) => void;
    /**
     * True while the agent has auto-escalated this turn to deep research (the
     * dynamic-routing `deep_research` step is running) even though the operator
     * did not pick it. The menu then shows "Vertiefte Recherche". Transient:
     * clears when the step finishes.
     */
    researchActive?: boolean;
    /**
     * Push text back into the composer imperatively — e.g. to restore a message
     * the host failed to send (the composer clears optimistically on submit, so
     * a failed mutation would otherwise lose it). Pass a fresh object each time;
     * the effect keys on identity so re-restoring the same text still applies.
     */
    restore?: { text: string };
    /**
     * Enable file attachments (PDF / image / Word) by picker, paste and drop.
     * Requires `conversationId` for files to survive a reload; without one (the
     * very first message of a chat) uploads still work, they are just staged
     * unbound until the turn is sent. Omit to hide the paperclip (default).
     */
    allowAttachments?: boolean;
    /** Conversation the staged uploads belong to. Null before the chat exists. */
    conversationId?: string | null;
}

/** Screenshots arrive on the clipboard as files without a name; give them one so
 * the chip and the prompt can refer to them. */
function nameScreenshots(files: File[]): File[] {
    return files.map((file) =>
        file.name
            ? file
            : new File([file], `Screenshot.${file.type.split("/")[1] || "png"}`, {
                  type: file.type,
              }),
    );
}

/** The assistant chat's composer (v2 ChatComposer) with its draft, uploads and the
 * answer-mode menu. */
export function AssistantChatInput({
    onSubmit,
    placeholder,
    disabled = false,
    isSending = false,
    generating = false,
    size = "md",
    draftKey,
    showResearchToggle = false,
    researchMode = false,
    onResearchModeChange,
    researchActive = false,
    restore,
    allowAttachments = false,
    conversationId = null,
}: AssistantChatInputProps) {
    const { t } = useTranslation();
    const [inputText, setInputText] = useState(() =>
        draftKey ? loadAssistantDraft(draftKey) : "",
    );
    const { attachments, isUploading, readyIds, addFiles, removeAttachment, clearStaged } =
        useChatAttachments({ conversationId });

    // Restore the persisted draft when the chat context changes (e.g. operator
    // switches tickets, or the conversation id resolves after refresh). The
    // text for the previous key was already persisted on each keystroke.
    useEffect(() => {
        setInputText(draftKey ? loadAssistantDraft(draftKey) : "");
    }, [draftKey]);

    // Restore text the host asked us to put back (e.g. a failed send). Keyed on
    // the object identity so the host can re-restore identical text.
    useEffect(() => {
        if (restore?.text) setInputText(restore.text);
    }, [restore]);

    const handleChange = (value: string) => {
        setInputText(value);
        if (draftKey) saveAssistantDraft(draftKey, value);
    };

    const handleSubmit = () => {
        const text = inputText.trim();
        // A message may be files-only: the operator drops a datasheet and asks about
        // it in the next turn. Still blocked while an upload is in flight, so the
        // turn cannot be sent referencing a file the model will not receive.
        if (disabled || isSending || isUploading) return;
        if (!text && readyIds.length === 0) return;
        const ids = readyIds;
        setInputText("");
        if (draftKey) clearAssistantDraft(draftKey);
        clearStaged();
        onSubmit(text, ids.length > 0 ? ids : undefined);
    };

    const labels: ChatComposerLabels = {
        input: t("assistantChatInput.label", { defaultValue: "Nachricht an lytra" }),
        placeholder: placeholder ?? t("assistantChatInput.placeholder"),
        send: t("assistantChatInput.send", { defaultValue: "Senden" }),
        stop: t("assistantChatInput.stop", { defaultValue: "Antwort stoppen" }),
        attach: t("assistantChatInput.attach", { defaultValue: "Datei anhängen (PDF, Bild, Word)" }),
        remove: (name) => t("assistantChatInput.remove", { name, defaultValue: `${name} entfernen` }),
        attachments: t("assistantChatInput.attachments", { defaultValue: "Anhänge" }),
        dropHint: t("assistantChatInput.dropHint", { defaultValue: "Dateien hier ablegen" }),
        uploading: (progress) => `${progress} %`,
        failed: t("assistantChatInput.failed", { defaultValue: "fehlgeschlagen" }),
    };

    const deep = researchMode || researchActive;

    return (
        <ChatComposer
            size={size}
            value={inputText}
            onValueChange={handleChange}
            onSubmit={handleSubmit}
            sending={isSending}
            generating={generating}
            disabled={disabled}
            attachments={attachments.map((a) => ({
                id: a.key,
                name: a.name,
                status: a.status,
                progress: a.progress,
                kind: a.contentType.startsWith("image/") ? "image" : "file",
            }))}
            onRemoveAttachment={removeAttachment}
            onAddFiles={
                allowAttachments ? (files) => addFiles(nameScreenshots(files)) : undefined
            }
            accept={CHAT_ATTACHMENT_ACCEPT}
            toolbar={
                showResearchToggle && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                leadingIcon={deep ? Telescope : Zap}
                                trailingIcon={ChevronDown}
                            >
                                {deep
                                    ? t("assistantChatInput.deepResearch")
                                    : t("assistantChatInput.quickAnswer")}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuRadioGroup
                                value={researchMode ? "deep" : "quick"}
                                onValueChange={(mode) => onResearchModeChange?.(mode === "deep")}
                            >
                                <DropdownMenuRadioItem value="quick">
                                    {t("assistantChatInput.quickAnswer")}
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="deep">
                                    {t("assistantChatInput.deepResearch")}
                                </DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )
            }
            labels={labels}
        />
    );
}
